import { format } from 'date-fns';
import { ForwardedRef, forwardRef } from 'react';
import { useNavigate } from 'react-router';
import { FETCH_JOBS_WITH_TRANSACTIONS, FETCH_RECEIPT_LIST } from '~/actions';
import { Box, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CenteredSpinner, DataGrid, SortingArrow, TSortValue } from '~/commonComponents';
import { useAppSelector, useLoading } from '~/hooks';
import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { IJobsWithTransaction } from '~/models';
import { Colors } from '~/themes/colors';
import { getJobStatus } from '../../utils';

interface IPostingPaymentHistoryTableProps {
  sort: TSortValue;
  onSort: (field: string) => void;
  onScroll: () => void;
  /** Bounds the table's height so it scrolls internally (enables infinite scroll). */
  maxHeight?: number;
}

export const PostingPaymentHistoryTable = forwardRef<
  HTMLDivElement,
  IPostingPaymentHistoryTableProps
>(({ sort, onScroll, onSort, maxHeight }, ref: ForwardedRef<HTMLDivElement>) => {
  const navigate = useNavigate();

  const transactions: IJobsWithTransaction[] = useAppSelector(
    (state) => state.job.jobsWithTransaction,
  );
  const { isLoading } = useLoading([FETCH_RECEIPT_LIST, FETCH_JOBS_WITH_TRANSACTIONS]);

  // Only blank the grid with the full-screen loader on the initial load. While paginating
  // (data already on screen), keep the rows and show a spinner at the bottom instead of
  // replacing the whole table (GOTU-4127).
  const isInitialLoading = isLoading && transactions.length === 0;
  const isPaginating = isLoading && transactions.length > 0;

  return (
    <Column
      data-cy="shift-receipts-table"
      data-testid="shift-receipts-table"
      ref={ref}
      onScroll={onScroll}
      style={{
        marginBottom: 20,
        overflowX: 'auto',
        overflowY: 'auto',
        width: '100%',
        minHeight: 100,
        maxHeight,
      }}
    >
      <DataGrid
        columns={[
          {
            key: 'office',
            header: 'Office',
            render: (item) => (
              <Typography as="span" data-cy="office-name-column" data-testid="office-name-column">
                {item?.offer_owner?.office_name}
              </Typography>
            ),
          },
          {
            key: 'type',
            header: 'Type',
            width: '140px',
            render: (item: IJobsWithTransaction) => (
              <Box
                style={{
                  alignSelf: 'flex-start',
                  padding: '2px 10px',
                  borderRadius: 8,
                  backgroundColor: item.isOnsiteInterview
                    ? Colors.jobs_interview_background
                    : Colors.jobs_shift_background,
                }}
              >
                <Typography as="span" color={colors.neutral[500]}>
                  {item.isOnsiteInterview ? 'INTERVIEW' : 'SHIFT'}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'professional',
            header: 'Professional',
            width: '220px',
            render: (item) => (
              <Typography as="span" data-cy="professional-column" data-testid="professional-column">
                {item.offer_final_reciever || '-'}
              </Typography>
            ),
          },
          {
            key: 'date',
            header: (
              <Row gap={2}>
                <Typography as="span" bold color={colors.primary[500]} variant="body1">
                  Date
                </Typography>
                <SortingArrow
                  name="jobDate"
                  sorting={sort}
                  toggleOnly
                  onChange={(val) => onSort(val?.field || '')}
                />
              </Row>
            ),
            width: '200px',
            render: (item) => (
              <Typography as="span" data-cy="shift-date-column" data-testid="shift-date-column">
                {format(new Date(item.shift_date), 'EEE, MMM dd, yyyy')}
              </Typography>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            width: '120px',
            render: (item) => (
              <Typography
                as="span"
                bold
                color={getJobStatus(item.shift_status)?.color}
                data-cy="payment-status-column"
                data-testid="payment-status-column"
                size="caption_sm"
                uppercase
              >
                {getJobStatus(item.shift_status)?.label || item.shift_status}
              </Typography>
            ),
          },
          {
            key: 'transaction',
            header: 'Transaction',
            width: '110px',
            render: (item) => <Typography as="span">{item.no_transactions}</Typography>,
          },
          {
            key: 'action',
            header: '',
            width: '50px',
            align: 'center',
            render: () => <OutlineChevronRightIcon color={colors.secondary[500]} />,
          },
        ]}
        data={transactions}
        dataCy={{
          header: 'payment-history-table-header',
        }}
        dataTestId={{
          header: 'payment-history-table-header',
        }}
        keyExtractor={(item) => item.id}
        isLoading={isInitialLoading}
        isStickyHeader
        onRowClick={(item) => navigate(`/dashboard/receipts/transactions/${item.id}`)}
      />
      {isPaginating && (
        <Box style={{ marginTop: 24 }}>
          <CenteredSpinner />
        </Box>
      )}
    </Column>
  );
});

PostingPaymentHistoryTable.displayName = 'PostingPaymentHistoryTable';
